import {
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { assertNever, set } from "@impero-com/common/utils/obj";
import { nullToEmpty } from "@impero-com/common/utils/string";

import {
    flattenButtonsBehaviour,
    flattenElementsAndSectionsBehaviour,
} from "./behaviours";
import { ButtonPanel } from "./buttonPanel";
import { BaseContentPanelProps, ContentPanel } from "./contentPanel";
import { DraggedElement } from "./element";
import { DraggedSection } from "./section";
import * as theme from "./theme.css";
import {
    type ElementButtonSpec,
    type SectionSpecId,
    type ElementPosition,
    type ElementPlaceholderPosition,
    type PlaceholderPositionAndRender,
    type SectionPlaceholderPosition,
    type SectionPlaceholderPositionAndRender,
    type DraggedButton,
    ITEM_TYPES,
} from "./types";
import {
    getBigMovementDirection,
    isSameMovementDirection,
    DragPositionState,
    MovementDirection,
    NICE_MOVEMENT_THRESHOLD_PIXELS,
} from "./utils";

// Lets hope nobody ever creates more than Infinity elements in a control
const LAST_ROW_POSITION = Infinity;

type DraggedItem<ElementButtonSpecId extends string> =
    | DraggedButton<ElementButtonSpecId>
    | DraggedElement
    | DraggedSection;

type DroppableData =
    | {
          dropKind: "ELEMENT";
          sectionId: SectionSpecId;
          column: number;
          row: number;
      }
    | {
          dropKind: "COLUMN";
          sectionId: SectionSpecId;
          column: number;
      }
    | {
          dropKind: "SECTION";
          sectionId: SectionSpecId;
      }
    | { dropKind: "CONTAINER" };

function isDroppableData(
    data: Record<string | symbol, unknown>
): data is DroppableData {
    const dropKind = data.dropKind;
    return (
        dropKind === "ELEMENT" ||
        dropKind === "COLUMN" ||
        dropKind === "SECTION" ||
        dropKind === "CONTAINER"
    );
}

function isDraggedData<ElementButtonSpecId extends string>(
    data: object
): data is DraggedItem<ElementButtonSpecId> {
    const type = "type" in data ? data.type : undefined;
    return (
        type === ITEM_TYPES.BUTTON ||
        type === ITEM_TYPES.ELEMENT ||
        type === ITEM_TYPES.SECTION
    );
}

/**
 * An element designer component, with:
 * - a content panel, containing elements split into sections
 * - a buttons panel on the right, with draggable buttons
 */
export interface ElementDesignerProps<
    SEC,
    ELT,
    ElementButtonSpecId extends string,
> extends Omit<
    BaseContentPanelProps<SEC, ELT, ElementButtonSpecId>,
    "title" | "tooltip" | "renderSection" | "renderSectionPlaceholder"
> {
    contentClassName?: string;
    contentTitle: string;
    contentTooltip?: string;
    headerAction?: ReactNode;
    buttonPanelTitle: string;
    buttonPanelTooltip?: string;
    buttonSpecs: Array<ElementButtonSpec<ElementButtonSpecId>>;
    renderElementPlaceholder: (elt: ELT) => ReactElement;
    renderSection?: (
        sectionSpec: SEC,
        children: ReactElement | null
    ) => ReactElement;
    renderSectionPlaceholder?: (
        sectionSpec: SEC,
        children: ReactElement | null
    ) => ReactElement;
    children?: never;
    noElementsLabel?: string;
}

type ElementDesignerStatus<ElementButtonSpecId extends string> =
    | { type: "DEFAULT" }
    | {
          type: "DRAGGING_BUTTON";
          draggedButtonId: ElementButtonSpecId;
          placeholderPosition: ElementPlaceholderPosition | undefined;
          renderPlaceholder: () => ReactElement;
      }
    | {
          type: "DRAGGING_ELEMENT";
          draggedElementPosition: ElementPosition;
          placeholderPosition: ElementPlaceholderPosition | undefined;
          renderPlaceholder: () => ReactElement;
      }
    | {
          type: "DRAGGING_SECTION";
          draggedSectionIndex: number;
          placeholderPosition: SectionPlaceholderPosition | undefined;
      };

export function ElementDesigner<SEC, ELT, ElementButtonSpecId extends string>({
    sectionSpecs,
    buttonPanelTitle,
    buttonSpecs,
    buttonsBehaviour,
    contentTitle,
    elementsAndSectionsBehaviour,
    getColCount,
    getElementColumns,
    getElementId,
    getSectionId,
    renderElement,
    renderElementPlaceholder,
    buttonPanelTooltip,
    className,
    contentClassName,
    contentTooltip,
    headerAction,
    noElementsLabel,
    renderSection,
    renderSectionPlaceholder,
    sectionsClassName,
}: ElementDesignerProps<SEC, ELT, ElementButtonSpecId>) {
    const [status, setStatus] = useState<
        ElementDesignerStatus<ElementButtonSpecId>
    >({ type: "DEFAULT" });

    // Status mirror — monitor callbacks fire outside React's state batching;
    // reading from the closure would see stale values across rapid events.
    const statusRef = useRef(status);
    // eslint-disable-next-line react-hooks/refs
    statusRef.current = status;

    // Direction tracking across drop-target-change events. Mirrors what
    // section/element used to do with their own `lastYPosition` state under
    // react-dnd's per-target hover callbacks.
    const directionState = useRef<DragPositionState | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);

    // Keep the latest prop values reachable inside the monitor's callbacks
    // without re-subscribing on every render.
    const handlersRef = useRef({
        sectionSpecs,
        buttonSpecs,
        getSectionId,
        getElementColumns,
        renderElementPlaceholder,
        buttonsBehaviour,
        elementsAndSectionsBehaviour,
    });
    // eslint-disable-next-line react-hooks/refs
    handlersRef.current = {
        sectionSpecs,
        buttonSpecs,
        getSectionId,
        getElementColumns,
        renderElementPlaceholder,
        buttonsBehaviour,
        elementsAndSectionsBehaviour,
    };

    const handleHoverSection = useCallback(
        (
            item: DraggedSection,
            sectionId: SectionSpecId,
            verticalDirection: MovementDirection
        ): void => {
            const status = statusRef.current;
            const { sectionSpecs, getSectionId } = handlersRef.current;
            if (status.type !== "DRAGGING_SECTION") {
                return;
            }
            let skippedDraggedSection = false;
            const index = sectionSpecs.findIndex((section) => {
                const id = getSectionId(section);
                const match = id === sectionId;
                if (!match && id === item.id) {
                    skippedDraggedSection = true;
                }
                return match;
            });
            const indexAfterDraggedElementIsRemoved = skippedDraggedSection
                ? index - 1
                : index;
            const indexToUseOnHover =
                status.placeholderPosition &&
                status.placeholderPosition.indexAfterDraggedSectionRemoved <=
                    indexAfterDraggedElementIsRemoved
                    ? indexAfterDraggedElementIsRemoved + 1
                    : indexAfterDraggedElementIsRemoved;
            if (
                !status.placeholderPosition ||
                isSameMovementDirection(
                    status.placeholderPosition.indexAfterDraggedSectionRemoved,
                    indexToUseOnHover,
                    verticalDirection
                )
            ) {
                setStatus(
                    set(status, {
                        placeholderPosition: {
                            indexAfterDraggedSectionRemoved: indexToUseOnHover,
                        },
                    })
                );
            }
        },
        []
    );

    const handleHoverSectionColumn = useCallback(
        (
            item: DraggedButton<ElementButtonSpecId> | DraggedElement,
            sectionId: SectionSpecId,
            column: number
        ): void => {
            const status = statusRef.current;
            switch (item.type) {
                case ITEM_TYPES.BUTTON: {
                    if (status.type !== "DRAGGING_BUTTON") {
                        return;
                    }
                    if (
                        status.placeholderPosition &&
                        sectionId ===
                            status.placeholderPosition
                                .positionAfterDraggedElementIsRemoved
                                .sectionId &&
                        column ===
                            status.placeholderPosition
                                .positionAfterDraggedElementIsRemoved.location
                                .column
                    ) {
                        return;
                    }
                    setStatus(
                        set(status, {
                            placeholderPosition: {
                                positionAfterDraggedElementIsRemoved: {
                                    sectionId,
                                    location: {
                                        column,
                                        row: LAST_ROW_POSITION,
                                    },
                                },
                            },
                        })
                    );
                    break;
                }
                case ITEM_TYPES.ELEMENT: {
                    if (status.type !== "DRAGGING_ELEMENT") {
                        return;
                    }
                    if (
                        status.placeholderPosition &&
                        sectionId ===
                            status.placeholderPosition
                                .positionAfterDraggedElementIsRemoved
                                .sectionId &&
                        column ===
                            status.placeholderPosition
                                .positionAfterDraggedElementIsRemoved.location
                                .column
                    ) {
                        return;
                    }
                    setStatus(
                        set(status, {
                            placeholderPosition: {
                                positionAfterDraggedElementIsRemoved: {
                                    sectionId,
                                    location: {
                                        column,
                                        row: LAST_ROW_POSITION,
                                    },
                                },
                            },
                        })
                    );
                    break;
                }
                default: {
                    return assertNever(item);
                }
            }
        },
        []
    );

    const handleHoverElement = useCallback(
        (
            item: DraggedButton<ElementButtonSpecId> | DraggedElement,
            sectionId: SectionSpecId,
            column: number,
            row: number,
            verticalDirection: MovementDirection
        ): void => {
            const status = statusRef.current;
            switch (item.type) {
                case ITEM_TYPES.BUTTON: {
                    if (status.type !== "DRAGGING_BUTTON") {
                        return;
                    }
                    const targetRow = bumpRowPastPlaceholder(
                        status.placeholderPosition,
                        sectionId,
                        column,
                        row
                    );
                    commitElementPlaceholder(
                        status,
                        sectionId,
                        column,
                        targetRow,
                        verticalDirection
                    );
                    break;
                }
                case ITEM_TYPES.ELEMENT: {
                    if (status.type !== "DRAGGING_ELEMENT") {
                        return;
                    }
                    const draggedLocation =
                        status.draggedElementPosition.location;
                    // The dragged element is unmounted from its column; rows
                    // of following elements shift up by one visually but their
                    // data still carries the original row. Step 1: undo that.
                    const sameColumnAsDragged =
                        status.draggedElementPosition.sectionId === sectionId &&
                        draggedLocation.column === column;
                    const rowAfterDraggedRemoved =
                        sameColumnAsDragged && row > draggedLocation.row
                            ? row - 1
                            : row;
                    // Step 2: when the placeholder already sits at or before
                    // the hovered row in the same column, bump past so the
                    // user can place the item after the last element.
                    const targetRow = bumpRowPastPlaceholder(
                        status.placeholderPosition,
                        sectionId,
                        column,
                        rowAfterDraggedRemoved
                    );
                    commitElementPlaceholder(
                        status,
                        sectionId,
                        column,
                        targetRow,
                        verticalDirection
                    );
                    break;
                }
                default: {
                    return assertNever(item);
                }
            }

            function bumpRowPastPlaceholder(
                placeholder: ElementPlaceholderPosition | undefined,
                hoverSectionId: SectionSpecId,
                hoverColumn: number,
                baseRow: number
            ): number {
                if (!placeholder) {
                    return baseRow;
                }
                const p = placeholder.positionAfterDraggedElementIsRemoved;
                const sameColumn =
                    p.sectionId === hoverSectionId &&
                    p.location.column === hoverColumn;
                return sameColumn && p.location.row <= baseRow
                    ? baseRow + 1
                    : baseRow;
            }
            function commitElementPlaceholder(
                currentStatus:
                    | (ElementDesignerStatus<ElementButtonSpecId> & {
                          type: "DRAGGING_BUTTON";
                      })
                    | (ElementDesignerStatus<ElementButtonSpecId> & {
                          type: "DRAGGING_ELEMENT";
                      }),
                hoverSectionId: SectionSpecId,
                hoverColumn: number,
                targetRow: number,
                direction: MovementDirection
            ): void {
                const placeholder = currentStatus.placeholderPosition;
                if (
                    placeholder &&
                    !isSameMovementDirection(
                        placeholder.positionAfterDraggedElementIsRemoved
                            .location.row,
                        targetRow,
                        direction
                    )
                ) {
                    return;
                }
                if (
                    placeholder &&
                    hoverSectionId ===
                        placeholder.positionAfterDraggedElementIsRemoved
                            .sectionId &&
                    hoverColumn ===
                        placeholder.positionAfterDraggedElementIsRemoved
                            .location.column &&
                    targetRow ===
                        placeholder.positionAfterDraggedElementIsRemoved
                            .location.row
                ) {
                    return;
                }
                setStatus(
                    set(currentStatus, {
                        placeholderPosition: {
                            positionAfterDraggedElementIsRemoved: {
                                sectionId: hoverSectionId,
                                location: {
                                    column: hoverColumn,
                                    row: targetRow,
                                },
                            },
                        },
                    })
                );
            }
        },
        []
    );

    const handleInvalidateDrag = useCallback((): void => {
        const status = statusRef.current;
        switch (status.type) {
            case "DEFAULT":
                return;
            case "DRAGGING_BUTTON":
            case "DRAGGING_ELEMENT":
            case "DRAGGING_SECTION": {
                if (status.placeholderPosition === undefined) {
                    return;
                }
                setStatus(set(status, { placeholderPosition: undefined }));
                return;
            }
            default:
                return assertNever(status);
        }
    }, []);

    // Subscribe a single global monitor that owns all drag routing for this
    // designer instance — onDragStart sets status, onDropTargetChange routes
    // hover to the right handler, onDrop commits via the behaviour callbacks.
    useEffect(() => {
        return monitorForElements({
            canMonitor: ({ source }) => {
                const root = rootRef.current;
                if (!root) {
                    return false;
                }
                if (!root.contains(source.element)) {
                    return false;
                }
                return isDraggedData(source.data);
            },
            onDragStart: ({ source }) => {
                directionState.current = null;
                if (!isDraggedData<ElementButtonSpecId>(source.data)) {
                    return;
                }
                const data = source.data;
                switch (data.type) {
                    case ITEM_TYPES.BUTTON: {
                        const buttonSpec = handlersRef.current.buttonSpecs.find(
                            (spec) => spec.id === data.id
                        );
                        if (!buttonSpec) {
                            return;
                        }
                        setStatus({
                            type: "DRAGGING_BUTTON",
                            draggedButtonId: data.id,
                            placeholderPosition: undefined,
                            renderPlaceholder: buttonSpec.renderPlaceholder,
                        });
                        return;
                    }
                    case ITEM_TYPES.ELEMENT: {
                        const {
                            sectionSpecs,
                            getSectionId,
                            getElementColumns,
                            renderElementPlaceholder,
                        } = handlersRef.current;
                        const section = sectionSpecs.find(
                            (sectionSpec) =>
                                getSectionId(sectionSpec) ===
                                data.position.sectionId
                        );
                        if (!section) {
                            return;
                        }
                        const { column, row } = data.position.location;
                        const element = getElementColumns(section)[column][row];
                        setStatus({
                            type: "DRAGGING_ELEMENT",
                            draggedElementPosition: data.position,
                            placeholderPosition: undefined,
                            renderPlaceholder: () =>
                                renderElementPlaceholder(element),
                        });
                        return;
                    }
                    case ITEM_TYPES.SECTION: {
                        setStatus({
                            type: "DRAGGING_SECTION",
                            draggedSectionIndex: data.index,
                            placeholderPosition: undefined,
                        });
                        return;
                    }
                    default:
                        return assertNever(data);
                }
            },
            onDrag: ({ source, location }) => {
                if (!isDraggedData<ElementButtonSpecId>(source.data)) {
                    return;
                }
                const activeData = source.data;
                const dropTargets = location.current.dropTargets;
                const innermost = dropTargets[0];
                if (!innermost) {
                    // Pointer is outside every drop target — leave the
                    // placeholder in place so the user can drift back in.
                    return;
                }
                if (!isDroppableData(innermost.data)) {
                    return;
                }
                const overData = innermost.data;

                const direction = getBigMovementDirection(
                    location.current.input.clientY,
                    NICE_MOVEMENT_THRESHOLD_PIXELS,
                    directionState.current,
                    (next) => {
                        directionState.current =
                            typeof next === "function"
                                ? next(directionState.current)
                                : next;
                    }
                );

                switch (overData.dropKind) {
                    case "ELEMENT": {
                        if (
                            (activeData.type === ITEM_TYPES.BUTTON ||
                                activeData.type === ITEM_TYPES.ELEMENT) &&
                            direction !== null
                        ) {
                            handleHoverElement(
                                activeData,
                                overData.sectionId,
                                overData.column,
                                overData.row,
                                direction
                            );
                        }
                        return;
                    }
                    case "COLUMN": {
                        if (
                            activeData.type === ITEM_TYPES.BUTTON ||
                            activeData.type === ITEM_TYPES.ELEMENT
                        ) {
                            handleHoverSectionColumn(
                                activeData,
                                overData.sectionId,
                                overData.column
                            );
                        }
                        return;
                    }
                    case "SECTION": {
                        if (
                            activeData.type === ITEM_TYPES.SECTION &&
                            direction !== null
                        ) {
                            handleHoverSection(
                                activeData,
                                overData.sectionId,
                                direction
                            );
                        }
                        return;
                    }
                    case "CONTAINER": {
                        // Only invalidate for button/element drags — section
                        // drags drop on the container directly and shouldn't
                        // clear their placeholder.
                        if (activeData.type !== ITEM_TYPES.SECTION) {
                            handleInvalidateDrag();
                        }
                        return;
                    }
                    default:
                        return assertNever(overData);
                }
            },
            onDrop: ({ source }) => {
                const status = statusRef.current;
                if (!isDraggedData<ElementButtonSpecId>(source.data)) {
                    directionState.current = null;
                    setStatus({ type: "DEFAULT" });
                    return;
                }
                const activeData = source.data;
                const { buttonsBehaviour, elementsAndSectionsBehaviour } =
                    handlersRef.current;
                const { onAddButton } =
                    flattenButtonsBehaviour(buttonsBehaviour);
                const { onMoveElement, onMoveSection } =
                    flattenElementsAndSectionsBehaviour(
                        elementsAndSectionsBehaviour
                    );

                switch (activeData.type) {
                    case ITEM_TYPES.BUTTON: {
                        if (
                            status.type === "DRAGGING_BUTTON" &&
                            status.placeholderPosition &&
                            onAddButton
                        ) {
                            onAddButton(
                                activeData.id,
                                status.placeholderPosition
                                    .positionAfterDraggedElementIsRemoved
                                    .sectionId,
                                status.placeholderPosition
                                    .positionAfterDraggedElementIsRemoved
                                    .location
                            );
                        }
                        break;
                    }
                    case ITEM_TYPES.ELEMENT: {
                        if (
                            status.type === "DRAGGING_ELEMENT" &&
                            status.placeholderPosition &&
                            onMoveElement
                        ) {
                            onMoveElement(
                                activeData.id,
                                activeData.position,
                                status.placeholderPosition
                                    .positionAfterDraggedElementIsRemoved
                            );
                        }
                        break;
                    }
                    case ITEM_TYPES.SECTION: {
                        if (
                            status.type === "DRAGGING_SECTION" &&
                            status.placeholderPosition &&
                            onMoveSection
                        ) {
                            onMoveSection(
                                activeData.index,
                                status.placeholderPosition
                                    .indexAfterDraggedSectionRemoved
                            );
                        }
                        break;
                    }
                    default:
                        return assertNever(activeData);
                }
                directionState.current = null;
                setStatus({ type: "DEFAULT" });
            },
        });
    }, [
        handleHoverElement,
        handleHoverSection,
        handleHoverSectionColumn,
        handleInvalidateDrag,
    ]);

    let sectionPlaceholderPosition:
        | SectionPlaceholderPositionAndRender<SEC>
        | undefined;
    let draggedElementPosition: ElementPosition | undefined;
    let eltPlaceholderPositionAndRender:
        | PlaceholderPositionAndRender
        | undefined;
    const renderSectionWithFallback =
        renderSection ||
        ((sectionSpec: SEC, children: ReactElement | null) => (
            <div className={theme.section} id={getSectionId(sectionSpec)}>
                {children}
            </div>
        ));
    const renderSectionPlaceholderWithFallback =
        renderSectionPlaceholder ||
        ((sectionSpec: SEC, children: ReactElement | null) => (
            <div
                className={theme.sectionPlaceholder}
                id={getSectionId(sectionSpec)}
            >
                {children}
            </div>
        ));

    switch (status.type) {
        case "DRAGGING_BUTTON":
            draggedElementPosition = undefined;
            eltPlaceholderPositionAndRender = status.placeholderPosition
                ? {
                      type: "BUTTON_PLACEHOLDER",
                      position: status.placeholderPosition,
                      render: status.renderPlaceholder,
                  }
                : undefined;
            sectionPlaceholderPosition = undefined;
            break;
        case "DRAGGING_ELEMENT":
            draggedElementPosition = status.draggedElementPosition;
            eltPlaceholderPositionAndRender = status.placeholderPosition
                ? {
                      type: "ELEMENT_PLACEHOLDER",
                      position: status.placeholderPosition,
                      render: status.renderPlaceholder,
                  }
                : undefined;
            sectionPlaceholderPosition = undefined;
            break;
        case "DRAGGING_SECTION":
            draggedElementPosition = undefined;
            eltPlaceholderPositionAndRender = undefined;
            sectionPlaceholderPosition = status.placeholderPosition
                ? {
                      position: status.placeholderPosition,
                      draggedSectionIndex: status.draggedSectionIndex,
                      render: renderSectionPlaceholderWithFallback,
                  }
                : undefined;
            break;
        case "DEFAULT":
            eltPlaceholderPositionAndRender = undefined;
            sectionPlaceholderPosition = undefined;
            break;
        default:
            assertNever(status);
    }

    return (
        <div
            ref={rootRef}
            className={`${theme.elementDesigner} ${nullToEmpty(className)}`}
        >
            <ContentPanel<SEC, ELT, ElementButtonSpecId>
                className={contentClassName}
                sectionsClassName={sectionsClassName}
                title={contentTitle}
                tooltip={contentTooltip}
                sectionSpecs={sectionSpecs}
                getSectionId={getSectionId}
                getElementColumns={getElementColumns}
                renderSection={renderSectionWithFallback}
                getElementId={getElementId}
                renderElement={renderElement}
                getColCount={getColCount}
                buttonsBehaviour={buttonsBehaviour}
                elementsAndSectionsBehaviour={elementsAndSectionsBehaviour}
                draggedElementPosition={draggedElementPosition}
                eltPlaceholderPositionAndRender={
                    eltPlaceholderPositionAndRender
                }
                sectionPlaceholderPosition={sectionPlaceholderPosition}
                noElementsLabel={noElementsLabel}
            />
            {buttonsBehaviour.type !== "HIDDEN" && (
                <ButtonPanel
                    panelTitle={buttonPanelTitle}
                    panelTooltip={buttonPanelTooltip}
                    headerAction={headerAction}
                    buttonSpecs={buttonSpecs}
                    draggable={buttonsBehaviour.type === "DRAGGABLE"}
                />
            )}
        </div>
    );
}
